import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@design-system';
import { QuestionCombobox } from '@/features/quote-and-apply-builder';
import { Plus, X, Trash2 } from 'lucide-react';
import { COMPARISON_OPERATORS, useQuestionSectionMap } from '@/features/quote-and-apply-builder';

interface ConditionalBuilderProps {
  open: boolean;
  title: string;
  visibleCondition?: Condition;
  requiredCondition?: Condition;
  showConditionalTextCondition?: Condition;
  questions: Record<string, PolicyFormQuestion>;
  onChangeVisible?: (condition?: Condition) => void;
  onChangeRequired?: (condition?: Condition) => void;
  onChangeShowConditionalText?: (condition?: Condition) => void;
  onClose: () => void;
}

interface ConditionEditorProps {
  condition?: Condition;
  questions: Record<string, PolicyFormQuestion>;
  onChange: (condition?: Condition) => void;
}

function FieldConditionRow({
  condition,
  questions,
  onChange,
  onDelete,
}: {
  condition: FieldCondition;
  questions: Record<string, PolicyFormQuestion>;
  onChange: (condition: FieldCondition) => void;
  onDelete?: () => void;
}) {
  const selectedQuestion = questions[condition.field];
  const questionSectionMap = useQuestionSectionMap();

  const questionOptions = Object.values(questions).map(question => ({
    value: question.questionRef,
    label: question.questionRef,
    selectedDisplay: question.questionRef,
    optionDisplay: (
      <div className="flex w-64 flex-col gap-1">
        <span className="text-sm font-medium text-gray-950">{question.questionRef}</span>
        <span className="text-foreground truncate text-xs">{question.title}</span>
        {questionSectionMap[question.questionRef] && (
          <span className="text-primary truncate text-xs">
            {questionSectionMap[question.questionRef]}
          </span>
        )}
      </div>
    ),
  }));

  const showValueInput = !['empty', 'notEmpty'].includes(condition.operator);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <QuestionCombobox
        value={condition.field}
        options={questionOptions}
        onChange={val => onChange({ ...condition, field: val as string, value: '' })}
        className="w-48"
      />

      <Select
        value={condition.operator}
        onValueChange={op => onChange({ ...condition, operator: op as ComparisonOperator })}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {COMPARISON_OPERATORS.map(op => (
            <SelectItem
              key={op.value}
              value={op.value}
            >
              {op.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {showValueInput &&
        (selectedQuestion?.type === 'select' || selectedQuestion?.type === 'radio' ? (
          <Select
            value={(condition.value as string) || ''}
            onValueChange={val => onChange({ ...condition, value: val })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select value" />
            </SelectTrigger>
            <SelectContent>
              {selectedQuestion.options?.map(opt => (
                <SelectItem
                  key={opt.id}
                  value={opt.id}
                >
                  {opt.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            value={(condition.value as string) || ''}
            onChange={event => onChange({ ...condition, value: event.target.value })}
            placeholder="Value"
          />
        ))}

      {onDelete && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

function LogicalConditionEditor({
  condition,
  questions,
  onChange,
  onDelete,
  depth = 0,
}: {
  condition: LogicalCondition;
  questions: Record<string, PolicyFormQuestion>;
  onChange: (condition: LogicalCondition) => void;
  onDelete?: () => void;
  depth?: number;
}) {
  const handleChildChange = (index: number, child?: Condition) => {
    const updated = [...condition.conditions];
    if (child) {
      updated[index] = child;
    } else {
      updated.splice(index, 1);
    }
    onChange({ ...condition, conditions: updated });
  };

  const addFieldCondition = () => {
    onChange({
      ...condition,
      conditions: [
        ...condition.conditions,
        { field: '', operator: 'equals', value: '' } as FieldCondition,
      ],
    });
  };

  const addLogicalGroup = () => {
    onChange({
      ...condition,
      conditions: [
        ...condition.conditions,
        { operator: 'and', conditions: [] } as LogicalCondition,
      ],
    });
  };

  return (
    <div className={`space-y-3 ${depth > 0 ? 'border-l-2 border-gray-200 pl-4' : ''}`}>
      <div className="flex items-center gap-2">
        <Select
          value={condition.operator}
          onValueChange={op => onChange({ ...condition, operator: op as 'and' | 'or' })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="and">AND</SelectItem>
            <SelectItem value="or">OR</SelectItem>
          </SelectContent>
        </Select>

        {onDelete && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {condition.conditions.map((child, index) => {
        if ('conditions' in child) {
          return (
            <LogicalConditionEditor
              key={index}
              condition={child as LogicalCondition}
              questions={questions}
              onChange={updated => handleChildChange(index, updated)}
              onDelete={() => handleChildChange(index)}
              depth={depth + 1}
            />
          );
        }
        return (
          <FieldConditionRow
            key={index}
            condition={child as FieldCondition}
            questions={questions}
            onChange={updated => handleChildChange(index, updated)}
            onDelete={() => handleChildChange(index)}
          />
        );
      })}

      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={addFieldCondition}
        >
          <Plus className="mr-1 h-3 w-3" />
          Condition
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={addLogicalGroup}
        >
          <Plus className="mr-1 h-3 w-3" />
          AND/OR Group
        </Button>
      </div>
    </div>
  );
}

function ConditionEditor({ condition, questions, onChange }: ConditionEditorProps) {
  if (!condition) {
    return (
      <div className="py-4 text-center">
        <p className="mb-3 text-sm text-gray-500">No condition defined</p>
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onChange({ field: '', operator: 'equals', value: '' } as FieldCondition)}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add Single Rule
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onChange({ operator: 'and', conditions: [] } as LogicalCondition)}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add AND/OR Group
          </Button>
        </div>
      </div>
    );
  }

  if ('conditions' in condition) {
    return (
      <div className="space-y-3">
        <LogicalConditionEditor
          condition={condition as LogicalCondition}
          questions={questions}
          onChange={updated => onChange(updated)}
          onDelete={() => onChange(undefined)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <FieldConditionRow
        condition={condition as FieldCondition}
        questions={questions}
        onChange={updated => onChange(updated)}
        onDelete={() => onChange(undefined)}
      />
      <Button
        variant="outline"
        size="sm"
        onClick={() =>
          onChange({
            operator: 'and',
            conditions: [condition, { field: '', operator: 'equals', value: '' }],
          } as LogicalCondition)
        }
      >
        <Plus className="mr-1 h-3 w-3" />
        Add More Conditions
      </Button>
    </div>
  );
}

export function ConditionalBuilder({
  open,
  title,
  visibleCondition,
  requiredCondition,
  showConditionalTextCondition,
  questions,
  onChangeVisible,
  onChangeRequired,
  onChangeShowConditionalText,
  onClose,
}: ConditionalBuilderProps) {
  const [activeTab, setActiveTab] = useState<'visible' | 'required' | 'showConditionalText'>(
    'visible',
  );

  const tabs = [
    ...(onChangeVisible ? [{ id: 'visible', label: 'Visibility' }] : []),
    ...(onChangeRequired ? [{ id: 'required', label: 'Required' }] : []),
    ...(onChangeShowConditionalText
      ? [{ id: 'showConditionalText', label: 'Conditional Text' }]
      : []),
  ] as const;

  return (
    <Dialog
      open={open}
      onOpenChange={onClose}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={val => setActiveTab(val as typeof activeTab)}
        >
          <TabsList>
            {tabs.map(tab => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {onChangeVisible && (
            <TabsContent value="visible">
              <div className="mt-4">
                <p className="mb-4 text-sm text-gray-500">
                  Define when this item should be visible.
                </p>
                <ConditionEditor
                  condition={visibleCondition}
                  questions={questions}
                  onChange={onChangeVisible}
                />
              </div>
            </TabsContent>
          )}

          {onChangeRequired && (
            <TabsContent value="required">
              <div className="mt-4">
                <p className="mb-4 text-sm text-gray-500">
                  Define when this item should be required.
                </p>
                <ConditionEditor
                  condition={requiredCondition}
                  questions={questions}
                  onChange={onChangeRequired}
                />
              </div>
            </TabsContent>
          )}

          {onChangeShowConditionalText && (
            <TabsContent value="showConditionalText">
              <div className="mt-4">
                <p className="mb-4 text-sm text-gray-500">
                  Define when to show conditional title/helper text.
                </p>
                <ConditionEditor
                  condition={showConditionalTextCondition}
                  questions={questions}
                  onChange={onChangeShowConditionalText}
                />
              </div>
            </TabsContent>
          )}
        </Tabs>

        <div className="flex justify-end border-t pt-4">
          <Button onClick={onClose}>Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
